import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../../context/BookingContext';
import { useAuth } from '../../context/AuthContext';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface Passenger {
    given_name: string;
    family_name: string;
    born_on: string;
    gender: 'm' | 'f';
    email: string;
    phone_number: string;
    nationality: string;
    passport_number: string;
    home_address: string;
    city: string;
    state: string;
    lga: string;
    passenger_type: 'adult' | 'child' | 'infant';
}

const emptyPassenger: Passenger = {
    given_name: '',
    family_name: '',
    born_on: '',
    gender: 'm',
    email: '',
    phone_number: '',
    nationality: 'NG',
    passport_number: '',
    home_address: '',
    city: '',
    state: '',
    lga: '',
    passenger_type: 'adult',
};

const nigerianStates = [
    'Abia','Adamawa','Akwa Ibom','Anambra','Bauchi','Bayelsa','Benue','Borno','Cross River',
    'Delta','Ebonyi','Edo','Ekiti','Enugu','FCT','Gombe','Imo','Jigawa','Kaduna','Kano',
    'Katsina','Kebbi','Kogi','Kwara','Lagos','Nasarawa','Niger','Ogun','Ondo','Osun','Oyo',
    'Plateau','Rivers','Sokoto','Taraba','Yobe','Zamfara'
];

export default function Checkout() {
    const navigate = useNavigate();
    const { cart, formatPrice } = useBooking();
    const { user } = useAuth();

    const [passengers, setPassengers] = useState<Passenger[]>([{ ...emptyPassenger }]);
    const [contactEmail, setContactEmail] = useState('');
    const [contactPhone, setContactPhone] = useState('');
    const [errors, setErrors] = useState<string[]>([]);

    // Pre-fill email from auth
    useEffect(() => {
        if (user?.email) {
            setContactEmail(user.email);
            setPassengers(prev => prev.map((p, i) => i === 0 ? { ...p, email: user.email! } : p));
        }
    }, [user]);

    const handlePassengerChange = (index: number, field: keyof Passenger, value: string) => {
        setPassengers(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const addPassenger = () => {
        setPassengers(prev => [...prev, { ...emptyPassenger }]);
    };

    const removePassenger = (index: number) => {
        if (passengers.length > 1) {
            setPassengers(prev => prev.filter((_, i) => i !== index));
        }
    };

    const calculateTotal = () => cart.reduce((sum, item) => sum + item.price, 0);

    const validate = (): boolean => {
        const errs: string[] = [];
        if (!contactEmail) errs.push('Contact email is required');
        if (!contactPhone) errs.push('Contact phone is required');

        passengers.forEach((p, i) => {
            if (!p.given_name) errs.push(`Passenger ${i+1}: First name is required`);
            if (!p.family_name) errs.push(`Passenger ${i+1}: Last name is required`);
            if (!p.born_on) errs.push(`Passenger ${i+1}: Date of birth is required`);
            if (!p.phone_number) errs.push(`Passenger ${i+1}: Phone number is required`);
        });

        setErrors(errs);
        return errs.length === 0;
    };

    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);

    const handleProceedToPayment = () => {
        if (!validate()) return;
        
        if (!user) {
            setShowAuthModal(true);
            return;
        }

        navigate('/booking/payment', {
            state: {
                passengers,
                contactEmail,
                contactPhone,
                totalAmount: calculateTotal(),
                cartItems: cart
            }
        });
    };

    const handleGoogleAuth = async () => {
        setAuthLoading(true);
        try {
            const { signInWithPopup, googleProvider, auth } = await import('../../services/firebase');
            const { apiFetch } = await import('../../services/api');
            await signInWithPopup(auth, googleProvider);
            await apiFetch('/auth/sync-profile', { method: 'POST' });
            setShowAuthModal(false);
            // After auth, they can click proceed again, or we can auto-proceed:
            // navigate('/booking/payment', {...}) 
            // but it's safer to let the context update and they just click the button again.
        } catch (err) {
            console.error('Auth error during checkout:', err);
        } finally {
            setAuthLoading(false);
        }
    };

    if (cart.length === 0) {
        return (
            <>
                <Header />
                <div className="min-h-screen pt-24 pb-12 px-4 flex flex-col items-center justify-center" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d50 100%)' }}>
                    <div className="glass rounded-2xl p-12 text-center max-w-md" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <i className="ri-shopping-cart-line text-6xl text-amber-400 mb-4 block"></i>
                        <h2 className="text-2xl font-bold text-white mb-3">Your Cart is Empty</h2>
                        <p className="text-slate-400 mb-6">Search for flights or hotels to get started</p>
                        <button
                            onClick={() => navigate('/general')}
                            className="px-8 py-3 rounded-xl font-semibold text-white"
                            style={{ background: 'linear-gradient(135deg, #d4a853 0%, #b8902e 100%)' }}
                        >
                            Browse Flights & Hotels
                        </button>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="min-h-screen pt-24 pb-12 px-4" style={{ background: 'linear-gradient(135deg, #0a1628 0%, #1a2d50 100%)' }}>
                <div className="max-w-5xl mx-auto">
                    {/* Page Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-white mb-2">
                            <i className="ri-file-list-3-line mr-2 text-amber-400"></i>
                            Complete Your Booking
                        </h1>
                        <p className="text-slate-400">Fill in passenger details to proceed to payment</p>
                    </div>

                    {errors.length > 0 && (
                        <div className="mb-6 rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                            {errors.map((e, i) => <p key={i} className="text-red-300 text-sm">• {e}</p>)}
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Left: Forms */}
                        <div className="lg:col-span-2 space-y-6">

                            {/* Contact Information */}
                            <div className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                    <i className="ri-contacts-line text-amber-400"></i>
                                    Contact Information
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-400 mb-1">Email Address *</label>
                                        <input
                                            type="email"
                                            value={contactEmail}
                                            onChange={(e) => setContactEmail(e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                            placeholder="john@example.com"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-400 mb-1">Phone Number *</label>
                                        <input
                                            type="tel"
                                            value={contactPhone}
                                            onChange={(e) => setContactPhone(e.target.value)}
                                            className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                            placeholder="+234 801 234 5678"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Passengers */}
                            {passengers.map((passenger, index) => (
                                <div key={index} className="rounded-2xl p-6" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                    <div className="flex justify-between items-center mb-4">
                                        <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                            <i className="ri-user-line text-amber-400"></i>
                                            Passenger {index + 1}
                                        </h2>
                                        {passengers.length > 1 && (
                                            <button onClick={() => removePassenger(index)} className="text-red-400 hover:text-red-300 text-sm">
                                                <i className="ri-delete-bin-line mr-1"></i> Remove
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* First Name */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">First Name *</label>
                                            <input
                                                type="text"
                                                value={passenger.given_name}
                                                onChange={(e) => handlePassengerChange(index, 'given_name', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                placeholder="As on passport"
                                            />
                                        </div>
                                        {/* Last Name */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Last Name *</label>
                                            <input
                                                type="text"
                                                value={passenger.family_name}
                                                onChange={(e) => handlePassengerChange(index, 'family_name', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                placeholder="As on passport"
                                            />
                                        </div>
                                        {/* DOB */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Date of Birth *</label>
                                            <input
                                                type="date"
                                                value={passenger.born_on}
                                                onChange={(e) => handlePassengerChange(index, 'born_on', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', colorScheme: 'dark' }}
                                            />
                                        </div>
                                        {/* Gender */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Gender *</label>
                                            <select
                                                value={passenger.gender}
                                                onChange={(e) => handlePassengerChange(index, 'gender', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                            >
                                                <option value="m">Male</option>
                                                <option value="f">Female</option>
                                            </select>
                                        </div>
                                        {/* Phone */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Phone Number *</label>
                                            <input
                                                type="tel"
                                                value={passenger.phone_number}
                                                onChange={(e) => handlePassengerChange(index, 'phone_number', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                placeholder="+234 801 234 5678"
                                            />
                                        </div>
                                        {/* Nationality */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Nationality</label>
                                            <input
                                                type="text"
                                                value={passenger.nationality}
                                                onChange={(e) => handlePassengerChange(index, 'nationality', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                placeholder="NG"
                                            />
                                        </div>
                                        {/* Passport Number */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Passport Number</label>
                                            <input
                                                type="text"
                                                value={passenger.passport_number}
                                                onChange={(e) => handlePassengerChange(index, 'passport_number', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                placeholder="A12345678"
                                            />
                                        </div>
                                        {/* Passenger Type */}
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Passenger Type</label>
                                            <select
                                                value={passenger.passenger_type}
                                                onChange={(e) => handlePassengerChange(index, 'passenger_type', e.target.value)}
                                                className="w-full px-4 py-2.5 rounded-xl text-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                            >
                                                <option value="adult">Adult</option>
                                                <option value="child">Child</option>
                                                <option value="infant">Infant</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Address Section */}
                                    <div className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                        <h3 className="text-sm font-semibold text-slate-400 mb-3">
                                            <i className="ri-map-pin-line mr-1 text-amber-400"></i> Home Address
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium text-slate-400 mb-1">Street Address</label>
                                                <input
                                                    type="text"
                                                    value={passenger.home_address}
                                                    onChange={(e) => handlePassengerChange(index, 'home_address', e.target.value)}
                                                    className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                    placeholder="123 Main Street"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-400 mb-1">State</label>
                                                <select
                                                    value={passenger.state}
                                                    onChange={(e) => handlePassengerChange(index, 'state', e.target.value)}
                                                    className="w-full px-4 py-2.5 rounded-xl text-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                >
                                                    <option value="">Select State</option>
                                                    {nigerianStates.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-400 mb-1">LGA</label>
                                                <input
                                                    type="text"
                                                    value={passenger.lga}
                                                    onChange={(e) => handlePassengerChange(index, 'lga', e.target.value)}
                                                    className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                    placeholder="Local Government Area"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-slate-400 mb-1">City</label>
                                                <input
                                                    type="text"
                                                    value={passenger.city}
                                                    onChange={(e) => handlePassengerChange(index, 'city', e.target.value)}
                                                    className="w-full px-4 py-2.5 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-amber-400 focus:outline-none"
                                                    style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)' }}
                                                    placeholder="City"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}

                            <button
                                onClick={addPassenger}
                                className="w-full py-3 rounded-xl font-medium text-amber-400 hover:text-amber-300 transition-colors"
                                style={{ background: 'rgba(212,168,83,0.1)', border: '1px dashed rgba(212,168,83,0.3)' }}
                            >
                                <i className="ri-add-line mr-1"></i> Add Another Passenger
                            </button>
                        </div>

                        {/* Right: Order Summary */}
                        <div className="lg:col-span-1">
                            <div className="rounded-2xl p-6 sticky top-24" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                    <i className="ri-shopping-cart-2-line text-amber-400"></i>
                                    Order Summary
                                </h2>
                                <div className="space-y-3 mb-6">
                                    {cart.map((item, index) => (
                                        <div key={index} className="flex justify-between items-start py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                            <div>
                                                <p className="font-medium text-white capitalize flex items-center gap-1">
                                                    <i className={`ri-${item.type === 'flight' ? 'flight-takeoff' : 'hotel'}-line text-amber-400`}></i>
                                                    {item.type}
                                                </p>
                                                <p className="text-slate-500 text-xs mt-1">
                                                    {item.type === 'flight' && `${item.details?.origin} → ${item.details?.destination}`}
                                                    {item.type === 'hotel' && item.details?.name}
                                                </p>
                                            </div>
                                            <p className="font-semibold text-amber-400">{formatPrice(item.price)}</p>
                                        </div>
                                    ))}
                                </div>
                                <div className="flex justify-between items-center font-bold text-xl pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                                    <span className="text-white">Total</span>
                                    <span className="text-amber-400">{formatPrice(calculateTotal())}</span>
                                </div>

                                <button
                                    onClick={handleProceedToPayment}
                                    className="w-full mt-6 py-3.5 rounded-xl font-bold text-white text-lg transition-all hover:shadow-lg hover:shadow-amber-500/20"
                                    style={{ background: 'linear-gradient(135deg, #d4a853 0%, #b8902e 100%)' }}
                                >
                                    <i className="ri-secure-payment-line mr-2"></i>
                                    Proceed to Payment
                                </button>

                                <p className="text-center text-slate-500 text-xs mt-3">
                                    <i className="ri-lock-line mr-1"></i>
                                    Your payment is secured by Paystack
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Professional Guest Checkout Auth Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/80 backdrop-blur-sm px-4">
                    <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl relative animate-fade-in-up border border-slate-100">
                        <button 
                            onClick={() => setShowAuthModal(false)}
                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 p-2"
                        >
                            <i className="ri-close-line text-2xl"></i>
                        </button>
                        
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-logo-gold-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <i className="ri-shield-user-fill text-3xl text-logo-gold-600"></i>
                            </div>
                            <h3 className="text-2xl font-bold text-navy-900 mb-2">Secure Your Booking</h3>
                            <p className="text-slate-500 text-sm">Create an account or sign in to save your booking details and manage your trip easily.</p>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={handleGoogleAuth}
                                disabled={authLoading}
                                className="w-full flex justify-center items-center py-3.5 px-4 border-2 border-slate-200 rounded-xl shadow-sm text-sm font-bold text-slate-700 bg-white hover:bg-slate-50 hover:border-slate-300 focus:outline-none transition-all disabled:opacity-50"
                            >
                                {authLoading ? (
                                    <i className="ri-loader-4-line animate-spin text-xl text-slate-500"></i>
                                ) : (
                                    <>
                                        <svg className="h-5 w-5 mr-3" viewBox="0 0 24 24">
                                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                        </svg>
                                        Continue with Google
                                    </>
                                )}
                            </button>
                            
                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
                                <div className="relative flex justify-center text-xs"><span className="px-4 bg-white text-slate-400 font-medium">OR</span></div>
                            </div>

                            <button
                                onClick={() => navigate('/auth/login', { state: { from: '/booking/checkout' } })}
                                className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-navy-900 hover:bg-navy-800 transition-colors"
                            >
                                <i className="ri-mail-line mr-2 text-lg"></i> Sign in with Email
                            </button>
                        </div>
                        
                        <p className="text-center text-xs text-slate-400 mt-6">
                            By continuing, you agree to our Terms of Service and Privacy Policy.
                        </p>
                    </div>
                </div>
            )}

            <Footer />
        </>
    );
}
